import isEqual from 'lodash/isEqual';
import compact from 'lodash/compact';
import { makeObservable, override } from 'mobx';

import { DiagnosisParams, Optional, ReferralData, ServiceData } from 'chaika-frontend-types/frontend-cms';
import { ReferralStatus, Locale } from 'chaika-enums';

import { store } from '@store';
import { ReferralList as ReferralListStore, ReferralList } from '@store/models/SystemFields/Referral';
import { GroupedListParams } from '@store/abstract/GroupedList';
import { AbstractField } from './AbstractField';
import { getTranslate, translate } from '@chaika/i18n/helper';

export class ReferralField extends AbstractField<ReferralList, ReferralData[]> {
    constructor(params: ReferralData[], name: string) {
        super(params, name);
        makeObservable(this);
    }

    @override
    public get hasData(): boolean {
        const {
            entry: { id: entryId },
        } = store;

        const referralList: ReferralListStore = this.getValue();
        const statuses = [ReferralStatus.Active, ReferralStatus.Executed, ReferralStatus.Expired];
        const entryReferralIds = referralList.getFilteredValuesIds({ statuses, entryId });

        return entryReferralIds.length > 0;
    }

    public getItem(id: string): GroupedListParams<ReferralData> {
        return this.fieldValue.getExisting(id);
    }

    public getPreview(): string {
        return this.fieldValue.ids.map(id => this.getItemRevision(id)).join(', ');
    }

    public getItemTitle(id: string): string {
        const { serviceStore, laboratoryStore } = store;
        const referral = this.fieldValue.getExisting(id);
        const service = this.getReferralService(id);
        let labTitle;
        let isLabService = false;

        if (service && serviceStore.isServiceLaboratory(service.id)) {
            isLabService = true;
            const laboratoryServiceId = referral.service?.laboratoryService?.id;
            const laboratoryId = laboratoryServiceId
                ? laboratoryStore.laboratoryServiceList.get(laboratoryServiceId)?.laboratoryId
                : undefined;

            labTitle = laboratoryId ? laboratoryStore.laboratoryList.get(laboratoryId)?.name : undefined;
        }

        return compact([
            service?.code,
            isLabService && translate('laboratory'),
            labTitle,
            referral.status === ReferralStatus.Expired && translate('overdue'),
            referral.reservationExist &&
                referral.status === ReferralStatus.Active &&
                `(${translate('hasReservation')})`,
        ]).join(' ');
    }

    public getRedesignItemTitle(id: string, skipLabInfo: boolean = false, locale?: Locale): string {
        const { serviceStore, laboratoryStore, entry } = store;
        const referral = this.fieldValue.getExisting(id);
        const service = this.getReferralService(id);
        let labTitle;
        let isLabService = false;

        if (service && serviceStore.isServiceLaboratory(service.id)) {
            isLabService = true;
            const laboratoryServiceId = referral.service?.laboratoryService?.id;
            const laboratoryId = laboratoryServiceId
                ? laboratoryStore.laboratoryServiceList.get(laboratoryServiceId)?.laboratoryId
                : undefined;

            labTitle = laboratoryId ? laboratoryStore.laboratoryList.get(laboratoryId)?.name : undefined;
        }

        const selectedLang = locale ?? entry.selectedLang;
        return compact([
            selectedLang
                ? (service?.nameTranslations?.find(it => it.locale === selectedLang)?.value ?? service?.name)
                : service?.name,
            !skipLabInfo && isLabService && labTitle && `${getTranslate('laboratory', locale)} ${labTitle}`,
            referral.status === ReferralStatus.Cancelled && `(${getTranslate('canceled', locale)})`,
            referral.status === ReferralStatus.Expired && `(${getTranslate('overdue', locale)})`,
            referral.reservationExist &&
                referral.status === ReferralStatus.Active &&
                `(${getTranslate('hasReservation', locale)})`,
        ]).join(' ');
    }

    public getPrintItemTitle(id: string, locale?: Locale): string {
        const referral = this.fieldValue.getExisting(id);
        let title = this.getRedesignItemTitle(id);

        if (referral.count > 1) {
            title += ` x${referral.count}`;
        }

        return referral.status === ReferralStatus.Executed ? `${title} (${getTranslate('performed', locale)})` : title;
    }

    public getLabTitleInfo(id: string): Optional<string> {
        const { laboratoryStore, serviceStore } = store;
        const service = this.getReferralService(id);
        let laboratoryId;

        if (service && serviceStore.isServiceLaboratory(service.id)) {
            const referral = this.fieldValue.getExisting(id);
            const laboratoryServiceId = referral.service?.laboratoryService?.id;
            laboratoryId = laboratoryServiceId
                ? laboratoryStore.laboratoryServiceList.get(laboratoryServiceId)?.laboratoryId
                : undefined;
        }

        return laboratoryId ? laboratoryStore.laboratoryList.get(laboratoryId)?.name : undefined;
    }

    public getItemRevision(id: string): string {
        const referral = this.fieldValue.getExisting(id);
        const service = this.getReferralService(id);

        return `${service?.name ?? '-'} ${referral.count}`;
    }

    public isEqual(value: ReferralList): boolean {
        const referrals = this.makeUpdateParams();

        return isEqual(referrals, value.serialize());
    }

    public getDiagnosis(_: string): (DiagnosisParams & { id: string; icd: string })[] {
        return [];
    }

    public getItemContent(id: string): string {
        const service = this.getReferralService(id);

        return service?.name ?? '';
    }

    public makeUpdateParams(): ReferralData[] {
        return this.fieldValue.serialize();
    }

    protected setFieldValue(value: ReferralData[]): void {
        this.fieldValue = new ReferralList(value);
    }

    private getReferralService(referralId: string): Optional<ServiceData> {
        const { serviceStore } = store;

        const referral = this.fieldValue.getExisting(referralId);

        return serviceStore.serviceList.get(referral.serviceId);
    }
}
